<template>
    <div>       
        <div class="w-full flex flex-col">
            <div class="modal-header flex items-center justify-between cursor-move">
                <h1 class="flex-1 text-2xl">Manage Leases</h1>
                <div @click="hideModal">
                    <inline-svg name="close" classes="w-5 h-5 text-white fill-current cursor-pointer"></inline-svg>
                </div>
            </div>
            <div class="w-full flex">
                <div class="modal-toolbar h-20 flex items-center justify-between">
                    <div class="w-full flex items-center justify-between">
                        <div class="w-full flex items-center">
                            <div v-if="sites.selectedSite.leases.length" class="flex items-center">
                                <select
                                    v-model="selectedLeaseId"
                                    @change="$emit('selectedLeaseChange', parseInt($event.target.value))"
                                    name="lease"
                                    class="mt-0 form-select form-select-blue">
                                        <option
                                            v-for="lease in sites.selectedSite.leases" :key="lease.id"
                                            :value="lease.id">
                                                {{ lease.name }}
                                        </option>
                                </select>
                            </div>
                            <button
                                v-if="canCreateLease"
                                @click.prevent="createLease"
                                class="ml-2 flex items-center btn btn-small btn-indigo">
                                    <inline-svg name="add" classes="w-4 h-4 text-white fill-current"></inline-svg>
                                    <span class="ml-2">New Lease</span>
                            </button>
                        </div>

                        <div v-if="section != 'lease-info' && leases.selectedLease != null" class="flex items-center text-sm">
                            <div class="bg-brand px-4 py-3 p-4 rounded shadow-md text-center text-white">
                                <p class="font-bold">{{ leases.selectedLease?.start_date | date }}</p>
                                <p class="text-xs">Start Date</p>
                            </div>
                            <div class="ml-3 bg-brand px-4 py-3 rounded shadow-md text-center text-white">
                                <p class="font-bold">{{ leases.selectedLease?.end_date | date }}</p>
                                <p class="text-xs">End Date</p>
                            </div>
                        </div>
                    </div>                            
                </div>
            </div>

            <div class="flex">
                <div class="modal-sidebar">
                    <ul>
                        <li @click="section = 'lease-info'">
                            <span :class="{ highlighted: section == 'lease-info' }">Lease Info</span>
                        </li>
                        <li v-if="aLeaseIsSelected" @click="section = 'units'">
                            <span :class="{ highlighted: section == 'units' }">Units</span> 
                        </li>
                        <li v-if="aLeaseIsSelected" @click="section = 'usage'">
                            <span :class="{ highlighted: section == 'usage' }">Usage</span> 
                        </li>
                        <li v-if="aLeaseIsSelected" @click="section = 'expenses'">
                            <span :class="{ highlighted: section == 'expenses' }">Expenses</span>
                        </li>
                        <li v-if="aLeaseIsSelected" @click="section = 'key-dates'">
                            <span :class="{ highlighted: section == 'key-dates' }">Key Dates</span>
                        </li>
                    </ul>
                </div>

                <div v-show="section == 'lease-info'" class="w-full" style="min-height: 200px">
                    <lease-info v-if="selectedLeaseId" :form-errors="errors"></lease-info>
                    <div v-if="! selectedLeaseId" class="flex flex-col h-full items-center justify-center space-y-2">
                        <span class="text-xl">No Leases Found</span>
                        <button 
                            v-if="canCreateLease"
                            type="button"
                            @click.prevent="createLease"
                            class="flex items-center btn btn-small btn-indigo">
                                <inline-svg name="add" classes="w-4 h-4 text-white fill-current"></inline-svg>
                                <span class="ml-2">Create Lease</span>
                        </button>
                    </div>
                </div>

                <div v-show="section == 'units'" class="w-full">
                    <lease-units :per-page="5"></lease-units>
                </div>

                <div v-show="section == 'usage'" class="w-full">
                    <lease-usage></lease-usage>
                </div>

                <div v-show="section == 'expenses'" class="w-full">
                    <lease-expenses
                        :form-errors="errors"
                        @selectedExpenseChange="selectedExpenseId = $event"
                    ></lease-expenses>
                </div>

                <div v-show="section == 'key-dates'" class="w-full">
                    <lease-key-dates
                        :form-errors="errors"
                        @selectedKeyDateChange="selectedKeyDateId = $event">
                    </lease-key-dates>
                </div>
            </div>
            <div class="bg-gray-100 p-4">
                <div class="text-right">
                    <button class="btn btn-secondary mr-2" @click.prevent="hideModal">Close</button>
                    <button v-if="aLeaseIsSelected || selectedLeaseId == 'new'" class="btn btn-green" @click.prevent="save">
                        <span v-if="loading">
                            <inline-svg name="loading-button"></inline-svg>                                
                        </span>
                        <span v-if="! loading">Save</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapState } from 'vuex';

    export default {
        components: {
        },

        props: {
            selectedSection: {
                type: String,
                default: 'lease-info'
            }
        },

        data() {
            return {
                section: this.selectedSection,
                selectedExpenseId: '',
                selectedKeyDateId: '',
                errors: '',
                loading: false           
            }
        },

        mounted() {
            this.setLeasedSquareFootage();

            this.$eventHub.$on('change-section', (section) => {
                this.section = section;
            });
        },

        watch: {
            selectedLeaseId() {
                this.setLeasedSquareFootage();
            },

            selectedExpenseId() {
                if (this.selectedExpenseId != 'new') {
                    this.$emit('expenseSaved', this.selectedExpenseId);
                }
            },

            selectedKeyDateId() {
                if (this.selectedKeyDateId != 'new') {
                    this.$emit('keyDateSaved', this.selectedKeyDateId);
                }
            }
        },

        computed: {
            ...mapState(['leases']),
            ...mapState(['sites']),

            canCreateLease() {
                return this.$gate.can('create', 'site');
            },
            selectedLeaseId: {
                get() {
                    return this.leases.selectedLease ? this.leases.selectedLease.id : '';
                },

                set(leaseId) {
                    this.leases.selectedLease = _.find(this.sites.selectedSite.leases, ['id', leaseId])
                }
            },

            aLeaseIsSelected() {
                return this.selectedLeaseId != 'new' && this.selectedLeaseId != ''
            },
        },

        methods: {
            setLeasedSquareFootage() {
                if (this.leases.selectedLease) {

                    this.leases.selectedLease.units = _.map(this.leases.selectedLease.units, (unit) => {
                        unit.leased_square_footage = unit.pivot.leased_square_footage;

                        return unit;
                    });
                }
            },

            createLease() {
                this.sites.selectedSite.leases.push({
                    id: 'new',
                    site_id: this.sites.selectedSite.id,
                    name: this.sites.selectedSite.leases.length == 0 ? 'Main Lease' : '',
                    units: [],
                    start_date: '',
                    end_date: '',
                    free_months: ''
                });

                this.selectedLeaseId = 'new';

                this.section = 'lease-info';
            },

            getSaveUrl() {
                let leaseId = this.leases.selectedLease.id;

                if (this.section == 'lease-info' || this.section == 'usage') {
                    return leaseId == "new" ? '/leases' : '/leases/' + leaseId;

                } else if (this.section == 'units') {
                    return '/leases/' + leaseId + '/units';

                } else if (this.section == 'expenses') {                    
                    return this.selectedExpenseId == "new" ? '/expenses' : '/expenses/' + this.selectedExpenseId;

                } else if (this.section == 'key-dates') {                    
                    return this.selectedKeyDateId == "new"
                        ? '/leases/' + leaseId + '/key-dates'
                        : '/leases/' + leaseId + '/key-dates/' + this.selectedKeyDateId;
                }
            },

            getSaveData() {
                if (this.section == 'lease-info' || this.section == 'usage') {
                    this.leases.selectedLease._method = this.leases.selectedLease.id == "new" ? "POST" : "PATCH";

                    return this.leases.selectedLease;

                } else if (this.section == 'units') {
                    this.leases.selectedLease._method = "PATCH";

                    return this.leases.selectedLease;

                } else if (this.section == 'expenses') {
                    let expense = _.find(this.leases.selectedLease.expenses, ['id', this.selectedExpenseId]);
                    
                    expense._method = this.selectedExpenseId == "new" ? "POST" : "PATCH";
                    expense.amount = Number.prototype.cleanseFloat(expense.amount_in_dollars) * 100;

                    return expense;
                } else if (this.section == 'key-dates') {
                    let keyDate = _.find(this.leases.selectedLease.key_dates, ['id', this.selectedKeyDateId]);
                    
                    keyDate._method = this.selectedKeyDateId == "new" ? "POST" : "PATCH";

                    return keyDate;
                }
            },

            save() {
                this.loading = true;
                
                axios.post(this.getSaveUrl(), this.getSaveData()).then((response) => {
                    this.sites.selectedSite = response.data.site || this.sites.selectedSite;
                    this.leases.selectedLease = response.data.lease || this.leases.selectedLease;

                    if (response.data.expense) {
                        this.leases.selectedLease = response.data.expensable;
                        _.remove(this.leases.selectedLease.expenses, expense => expense.id == this.selectedExpenseId);
                        this.leases.selectedLease.expenses.push(response.data.expense);
                        this.selectedExpenseId = response.data.expense ? response.data.expense.id : this.selectedExpenseId;
                    }

                    if (response.data.keyDate) {
                        this.selectedKeyDateId = response.data.keyDate ? response.data.keyDate.id : this.selectedKeyDateId;
                        this.leases.selectedLease.key_dates = response.data.keyDates;
                    }

                    this.$emit('leaseSaved', response.data.lease ? response.data.lease.id : this.selectedLeaseId);
                    this.$eventHub.$emit('leaseSaved');
                    this.$eventHub.$emit('reloadSite');

                    this.errors = '';

                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: response.data.message
                    });
                }).catch((error) => {
                    this.errors = error.response.data ? error.response.data.errors : '';

                    this.$notify({
                        type: 'error',
                        title: 'Error',
                        text: error.response.data.message
                    });
                }).finally(() => {
                    this.loading = false;
                });
            }
        }
    }
</script>